import { motion } from "framer-motion";
import { PackageCheck, Wrench, Compass, ArrowRight } from "lucide-react";
import { fadeUp, stagger, viewport, easeOut } from "@/lib/motion";
import { TID } from "@/constants/site";

const PATHS = [
  {
    id: "use",
    icon: PackageCheck,
    tag: "Ready to deploy",
    title: "Use What We've Built",
    body: "We develop our own AI products, purpose-built to modernize specific sectors — designed from the ground up around the real challenges people face. If your problem fits, you get technology that's already proven, ready to deploy faster than building from zero.",
  },
  {
    id: "build",
    icon: Wrench,
    tag: "Custom development",
    title: "Build What You Need",
    body: "Not every problem fits a template — and we don't pretend it does. When your challenge is specific, we design and build custom AI applications around it: your workflow, your data, and above all, the decisions that matter most. Not a generic solution wearing your logo.",
  },
];

const ADVISORY = {
  icon: Compass,
  label: "Advisory & strategy",
  body: "Not sure which path is right — or whether AI is the right move at all? We help you identify where AI can genuinely improve outcomes, and where it can't, before any commitment to build. Honest advice, before the investment — not after.",
};

const HowWePartner = ({}) => {
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="partner" className="relative py-28 lg:py-36 bg-[#E3F1F2] border-t border-black/5">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport}>
          <span className="badge-pill">
            <span className="h-1.5 w-1.5 rounded-full bg-[#43B0CE]" />
            How We Partner
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight max-w-4xl leading-[1.1]">
            Two ways to work with us. <span className="text-[#43B0CE]">One goal.</span>
          </h2>
          <p className="mt-6 max-w-3xl text-lg font-light leading-relaxed text-[#3C5156]">
            Sometimes the right AI solution already exists. Sometimes it needs to be built. Either way, our goal
            is the same: helping businesses and individuals make better-informed decisions — faster and with more
            confidence.
          </p>
        </motion.div>

        <motion.div
          variants={stagger(0.12)}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="mt-14 grid md:grid-cols-2 gap-5"
        >
          {PATHS.map((p) => (
            <motion.div
              key={p.id}
              data-testid={TID.partner.card(p.id)}
              variants={fadeUp}
              className="card-arch rounded-2xl p-7 flex flex-col"
            >
              <span className="icon-tile">
                <p.icon className="h-5 w-5" />
              </span>
              <span className="mt-6 font-mono-ed text-[10px] tracking-[0.2em] uppercase text-[#43B0CE]">{p.tag}</span>
              <h3 className="mt-2 font-display text-xl font-semibold text-[#0B2429]">{p.title}</h3>
              <p className="mt-4 text-[15px] leading-relaxed text-[#3C5156] flex-1">{p.body}</p>
              <span className="btn-circle mt-7 self-start">
                <ArrowRight className="h-4 w-4" />
              </span>
            </motion.div>
          ))}
        </motion.div>

        {/* Advisory supports either path */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="mt-6 rounded-2xl border border-black/10 bg-[#F4FAFB] p-6 flex items-start gap-4"
        >
          <span className="icon-tile">
            <ADVISORY.icon className="h-5 w-5" />
          </span>
          <div>
            <p className="font-mono-ed text-[10px] tracking-[0.2em] uppercase text-[#43B0CE]">{ADVISORY.label}</p>
            <p className="mt-2 text-[15px] leading-relaxed text-[#3C5156]">{ADVISORY.body}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: easeOut }}
          className="mt-14 rounded-3xl border border-[#7FD0E6]/20 bg-gradient-to-r from-[#F4FAFB] to-[#FFFFFF] p-10 lg:p-14 flex flex-col lg:flex-row lg:items-center justify-between gap-8"
        >
          <div className="max-w-2xl">
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#0B2429]">Our approach never changes.</h3>
            <p className="mt-4 text-[15px] leading-relaxed text-[#3C5156]">
              However we work together, it starts the same way: understanding the challenge in front of you — and
              the decisions it depends on — before proposing anything. Sometimes that means pointing you to
              something we've already built. Sometimes it's building something new. Sometimes it's a mix of both.
            </p>
          </div>
          <button
            data-testid={TID.partner.talkBtn}
            onClick={() => go("connect")}
            className="group btn-primary shrink-0 px-7 py-4 text-sm"
          >
            Not sure which fits? Let's talk
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default HowWePartner;
